import React from 'react';
import PropTypes from 'prop-types';

import Criterium from '@hydra/common/criterium/Criterium';
import CriteriumChart from '@hydra/common/criterium/CriteriumChart';
import { GoogleAnalyticsDataAuthWrapper } from '@hydra/common/components/GoogleAnalytics/GoogleAnalyticsDataWrapper';
import WorldMap from '@hydra/common/components/WorldMap';
import getGraphData from '@hydra/utils/getGraphData';
import ReviewSkeleton from '@hydra/common/components/ReviewSkeleton';

const monthlyReport = {
  reportType: 'ga',
  query: {
    metrics: 'ga:sessions',
    dimensions: 'ga:yearmonth, ga:country',
    sort: '-ga:sessions',
    'end-date': 'today',
    'start-date': '365daysAgo'
  }
};

const mapReport = {
  reportType: 'ga',
  query: {
    metrics: 'ga:sessions',
    dimensions: 'ga:countryIsoCode',
    sort: '-ga:sessions',
    'end-date': 'today',
    'start-date': '365daysAgo'
  }
};

function toChartData (rows) {
  if (!rows) {
    return [];
  }
  const countriesToShow = 8;
  const sortByXDesc = (a, b) => a.x - b.x;
  const byCountry = rows.reduce((acc, [googleDate, country, sessions = 0]) => {
    sessions = sessions || 0;
    if (acc[country] === undefined) {
      acc[country] = { name: country, data: [] };
    }
    acc[country].data.push({ googleDate, sessions });
    return acc;
  }, {});
  return Object.values(byCountry)
    .slice(0, countriesToShow)
    .map(({ data, name }) => ({
      name,
      data: data.map(({ googleDate, sessions = 0 }) => {
        sessions = sessions || 0;
        return getGraphData({ x: googleDate, y: sessions });
      }).sort(sortByXDesc)
    }));
}

function toMapData (rows) {
  if (!rows || rows.length === 0) {
    return [];
  }
  const countriesToHighlight = 15;
  return rows
    .map(([country, sessions]) => ({ country, value: parseInt(sessions, 10) / 1000000 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, countriesToHighlight);
}

function AudienceCountryGraph ({ data }) {
  const chartData = toChartData(data);
  return (
    <CriteriumChart
      type='line'
      frequency='month'
      hasLegend
      hasTooltip
      data={chartData}
    />
  );
}

function AudienceCountryMap ({ data }) {
  const chartData = toMapData(data);
  return <WorldMap highlights={chartData} />;
}

export function PjGaAudienceCountry ({ criterium }) {
  return (
    <Criterium criterium={criterium || { type: 'pj_ga_audience_country', score: null, importance: 0, solvability: 0 }} clickable={!!criterium}>
      {criterium
        ? (
          <GoogleAnalyticsDataAuthWrapper
            accessToken={criterium.data && criterium.data.accessToken}
            viewId={criterium.data && criterium.data.viewId}
            report={monthlyReport}
            Component={AudienceCountryGraph}
            loadingComponent={<ReviewSkeleton height={300} />}
          />
          )
        : <ReviewSkeleton height={300} />}
      {criterium
        ? (
          <GoogleAnalyticsDataAuthWrapper
            accessToken={criterium.data && criterium.data.accessToken}
            viewId={criterium.data && criterium.data.viewId}
            report={mapReport}
            Component={AudienceCountryMap}
            loadingComponent={<ReviewSkeleton height={433} />}
          />
          )
        : <ReviewSkeleton height={433} />}
    </Criterium>
  );
}

PjGaAudienceCountry.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      viewId: PropTypes.string,
      accessToken: PropTypes.string
    })
  })
};

export default React.memo(PjGaAudienceCountry);
